import React, { FC, useCallback, useState } from 'react';
import { AlertBar, Button, Icon, Typography } from '@packages/ui/shared';
import { ANALYTICS_EVENTS } from '@packages/core/analytics';
import { analyticsService } from '@web/services/singletons';
import { CaseListModalContent } from './case-list-modal-content';
import { colors } from '@packages/core/styles';
import { createUseStyles } from 'react-jss';
import { Image, ListGroup, ListGroupItem, Modal, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Typeahead } from 'react-bootstrap-typeahead';
import { VinVehicleDetails } from '../../../../components/vin-vehicle-details';
import TechlineCreateCase from './assets/Techline-createcase.png';
import TechlineNQR from './assets/Techline-NQR.png';
import TechlineQMR from './assets/Techline-QMR.png';

import type { RetailerProfileOption } from '@packages/domain/techline/members';
import { useStartNewCase } from '@web/techline/dashboard/hooks/use-start-new-case';
import { useNavigate } from 'react-router-dom';

const MIN_VIN_LENGTH = 8;
const MAX_VIN_LENGTH = 17;

const alertBarStyle = { marginBottom: 24 };

type ModalFormType = 'allCaseTypes' | 'disclaimer' | 'selectRetailerAndVin' | 'reviewPreviousCases';

type AlertBarMessage = {
    message: string;
};

interface Props {
    show: boolean;
    onHide: (caseNumber?: string) => void;
}

interface CaseModalSectionItemProps {
    title: string;
    content: Array<string>;
    imageUrl: string;
    buttonText: string;
    buttonKey: string;
    hasInfoIcon?: boolean;
}

const useStyles = createUseStyles({
    clearIcon: {
        backgroundColor: colors.white,
        cursor: 'pointer',
        position: 'absolute',
        right: 8,
        top: 8,
        zIndex: 2,
    },
    modalItem: {
        display: 'flex',
        flexDirection: 'row',
        backgroundColor: '#F8F8F8',
        padding: 16,
        paddingLeft: 0,
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 16,
        height: '160px',
    },
    image: {
        minHeight: '160px',
        maxWidth: '111px',
        objectFit: 'cover',
        flexShrink: 0,
    },
    content: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        color: '#1F518F',
    },
    titleRow: {
        display: 'flex',
        alignItems: 'center',
        marginBottom: 8,
    },
    bulletList: {
        backgroundColor: '#F8F8F8',
        paddingLeft: 0,
        marginBottom: 0,
    },
    listItem: {
        backgroundColor: '#F8F8F8',
        border: 'none',
        padding: '4px 0',
        display: 'flex',
        fontWeight: 400,
        fontSize: 15,
        gap: 8,
    },
    nqrTooltip: {
        fontSize: 12,
        padding: '8px 12px',
        textAlign: 'left',
        marginTop: '-4px !important',
        color: 'blue',
    },
});

export const CreateCaseModalV2: FC<Props> = ({ show, onHide }) => {
    const styles = useStyles();
    const navigate = useNavigate();
    const [currentFormType, setCurrentFormType] = useState<ModalFormType>('allCaseTypes');
    const [alertBarMessage, setAlertBarMessage] = useState<AlertBarMessage>({ message: '' });
    const {
        // Vin search
        selectVehicle,
        setVehicleDetails,
        setVinInputValue,
        validateVin,
        isVinCheckLoading,
        possibleVehicles,
        vehicleDetails,
        vinError,
        vinInputValue,

        // Retailer profiles
        fetchRetailerProfileOptions,
        isLoadingProfiles,
        retailerProfiles,

        // Retailer selection
        selectedRetailerProfile,
        setSelectedRetailerProfile,

        // Case creation
        createDraftCase,
        existingCases,
        fetchExistingCases,
        isCreatingCase,
        isLoadingExistingCases,

        // Lifecycle
        resetState,
    } = useStartNewCase();

    const isVinLengthValid = vinInputValue.length === MIN_VIN_LENGTH || vinInputValue.length === MAX_VIN_LENGTH;
    const isVinCheckButtonDisabled = isVinCheckLoading || !isVinLengthValid;
    const isCreateCaseButtonDisabled = !vehicleDetails || !vinInputValue || !selectedRetailerProfile[0];
    const showRetailerCloseIcon = selectedRetailerProfile.length > 0 && retailerProfiles.length > 1;

    const caseModalSectionData: CaseModalSectionItemProps[] = [
        {
            title: 'Techline Case if you need to:',
            content: ['Request diagnostic assistance'],
            imageUrl: TechlineCreateCase,
            buttonText: 'Create Techline Case',
            buttonKey: 'createCase',
        },
        {
            title: 'Quality Monitoring Report if you need to:',
            content: [
                'Report customer or repair concerns related to a Subaru vehicle.',
                'Submit documents to support a Technical Service Bulletin (TSB) or a recall/service campaign, as directed by Subaru of America.',
            ],
            imageUrl: TechlineQMR,
            buttonText: 'Create QMR',
            buttonKey: 'qmr',
        },
        {
            title: 'Non-Quality Report if you need to:',
            content: [
                'Request release of a part on hold for a TSB, recall or service campaign.',
                'Submit files for DSQM review to support warranty preauthorization before repair.',
                'Document completed recall repairs.',
            ],
            imageUrl: TechlineNQR,
            buttonText: 'Create NQR',
            hasInfoIcon: true,
            buttonKey: 'nqr',
        },
    ];

    const handleCaseSelectionClick = (buttonKey: string) => {
        switch (buttonKey) {
            case 'nqr':
                handleHide();
                navigate('/nqrs');
                break;
            case 'qmr':
                handleHide();
                navigate('/qmrs');
                break;
            case 'createCase':
                setCurrentFormType('disclaimer');
                break;
        }
    };

    const handleAcceptDisclaimer = useCallback(async () => {
        const success = await fetchRetailerProfileOptions();
        if (success) {
            setCurrentFormType('selectRetailerAndVin');
        } else {
            setAlertBarMessage({
                message: 'An error occurred while fetching retailers. Please refresh the page and try again.',
            });
            setTimeout(() => setAlertBarMessage({ message: '' }), 3000);
        }
    }, [fetchRetailerProfileOptions]);

    /**
     * Checks for existing cases with the same retailer and VIN. If any are found, shows the list to the user.
     * If none are found, creates a new draft case and navigates to it.
     */
    const handleRequestNewCase = useCallback(async () => {
        if (isLoadingExistingCases || isCreatingCase) {
            return;
        }

        const cases = await fetchExistingCases();

        if (!cases) {
            setAlertBarMessage({
                message: 'An error occurred while checking existing cases. Please try again.',
            });
            setTimeout(() => setAlertBarMessage({ message: '' }), 3000);
            return;
        }

        if (cases.length > 0) {
            setCurrentFormType('reviewPreviousCases');
            return;
        }

        const caseNumber = await createDraftCase();
        if (caseNumber) {
            onHide(caseNumber);
        } else {
            setAlertBarMessage({
                message: 'An error occurred while creating the case. Please refresh the page and try again.',
            });
            setTimeout(() => setAlertBarMessage({ message: '' }), 3000);
        }
    }, [fetchExistingCases, createDraftCase, onHide]);

    /**
     * Creates a new case and navigates to it. Called when the user confirms
     * creating a new case from the review previous cases screen.
     */
    const handleConfirmNewCase = useCallback(async () => {
        const caseNumber = await createDraftCase();
        if (caseNumber) {
            onHide(caseNumber);
        } else {
            setAlertBarMessage({
                message: 'An error occurred while creating the case. Please refresh the page and try again.',
            });
            setTimeout(() => setAlertBarMessage({ message: '' }), 3000);
        }
    }, [createDraftCase, onHide]);

    const handleEditPreviousCase = useCallback(
        (caseNumber: string) => {
            if (!caseNumber) {
                return;
            }
            analyticsService.logEvent(ANALYTICS_EVENTS.USER_OPENS_EXISTING_CASE);
            onHide(caseNumber);
        },
        [onHide]
    );

    const handleRetailerTypeaheadChange = useCallback(
        (selected: RetailerProfileOption[]) => {
            setSelectedRetailerProfile(selected);
            if (selected.length > 0) {
                analyticsService.logEvent(ANALYTICS_EVENTS.USER_SELECTS_RETAILER);
            }
        },
        [setSelectedRetailerProfile]
    );

    const handleBackButtonClick = useCallback(
        (modalFormType: ModalFormType, reset: boolean) => {
            if (reset) {
                resetState();
            }
            setCurrentFormType(modalFormType);
        },
        [resetState]
    );

    const handleClearRetailer = useCallback(() => {
        setSelectedRetailerProfile([]);
    }, [setSelectedRetailerProfile]);

    const handleHide = useCallback(() => {
        setAlertBarMessage({ message: '' });
        resetState();
        onHide();
    }, [resetState, onHide]);

    const handleAlertBarClose = useCallback(() => {
        setAlertBarMessage({ message: '' });
    }, []);

    const renderAlertBar = () =>
        alertBarMessage.message ? (
            <AlertBar error={true} onClose={handleAlertBarClose} show={true} style={alertBarStyle}>
                <Typography>{alertBarMessage.message}</Typography>
            </AlertBar>
        ) : null;

    const renderCaseDisclaimerModal = () => {
        return (
            <React.Fragment>
                <Modal.Header>
                    <Modal.Title>Create a new case</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    {renderAlertBar()}
                    <Typography>
                        Welcome to Subaru Techline on TechShare for Subaru Retailers.
                        <br />
                        <br />
                        <span>
                            Disclaimer: Any information or advice furnished by the Subaru Technical Helpline is given
                            for the purpose of assisting technicians in the repair of a vehicle. Such information is not
                            intended to authorize repairs or to approve a repair procedure that a technician, in their
                            sole discretion has decided to perform.
                        </span>
                    </Typography>
                </Modal.Body>

                <Modal.Footer>
                    <div className="d-flex justify-content-end w-100">
                        <div className="d-flex">
                            <div className="mr-2">
                                <Button variant="ghost-blue" onPress={handleHide}>
                                    Cancel
                                </Button>
                            </div>
                            <Button variant="primary" onPress={handleAcceptDisclaimer} isLoading={isLoadingProfiles}>
                                Accept
                            </Button>
                        </div>
                    </div>
                </Modal.Footer>
            </React.Fragment>
        );
    };

    const caseModalSectionItem = ({
        title,
        content,
        imageUrl,
        buttonText,
        hasInfoIcon,
        buttonKey,
    }: CaseModalSectionItemProps) => (
        <div className={styles.modalItem}>
            <Image alt={title} src={imageUrl} className={styles.image} />
            <div className={styles.content}>
                <div className={styles.titleRow}>
                    <Typography variant="h6" style={{ color: '#1F518F' }}>
                        {title}
                    </Typography>
                    {hasInfoIcon && (
                        <OverlayTrigger
                            placement="bottom"
                            trigger={['click']}
                            overlay={
                                <Tooltip id="techlineNQR-info" className={styles.nqrTooltip}>
                                    <span>
                                        A Non Quality Report is for reviewing diagnoses that require pre-authorization,
                                        it is not a repair approval. Claims preauthorization is required for all final
                                        warranty approvals. See section 4.1. of Policy and Procedure manual for
                                        additional details.
                                    </span>
                                </Tooltip>
                            }
                        >
                            <Icon name="info" style={{ marginLeft: 8 }} />
                        </OverlayTrigger>
                    )}
                </div>
                <ListGroup className={styles.bulletList}>
                    {content.map((item, index) => (
                        <ListGroupItem key={index} className={styles.listItem}>
                            <Typography>{'\u2022'}</Typography>
                            <Typography style={{ fontWeight: 400, fontSize: 15, color: '#4C525D' }}>{item}</Typography>
                        </ListGroupItem>
                    ))}
                </ListGroup>
            </div>
            <Button variant="primary-blue-outline" onPress={() => handleCaseSelectionClick(buttonKey)}>
                {buttonText}
            </Button>
        </div>
    );

    const renderAllCaseTypes = () => {
        return (
            <React.Fragment>
                <Modal.Header>
                    <Modal.Title>Create new...</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {caseModalSectionData.map((item, index) => (
                        <div key={index} className="mb-3">
                            {caseModalSectionItem(item)}
                        </div>
                    ))}
                    <Typography variant="caption" style={{ fontSize: 15, color: '#071424' }}>
                        *Note: Once you select a Case/Report type, you'll be taken to its corresponding creation flow
                        and section.
                    </Typography>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="ghost-blue" onPress={() => onHide()}>
                        Cancel
                    </Button>
                </Modal.Footer>
            </React.Fragment>
        );
    };

    const renderSelectRetailerVinDetails = () => {
        return (
            <React.Fragment>
                <Modal.Header>
                    <Modal.Title>Create a new case</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {renderAlertBar()}
                    <div className="pb-4">
                        <div className="pb-2">
                            <Typography variant="labelRegular">
                                Retailer
                                <Typography color="redOne"> *</Typography>
                            </Typography>
                        </div>
                        <Typeahead
                            id="retailer-typeahead"
                            placeholder={retailerProfiles.length > 1 ? 'Select Retailer' : 'No Retailers found'}
                            options={retailerProfiles}
                            labelKey={(option) => `${option.retailerName} (#${option.retailerNumber})`}
                            onChange={handleRetailerTypeaheadChange}
                            selected={selectedRetailerProfile}
                            disabled={retailerProfiles.length <= 1}
                        >
                            {showRetailerCloseIcon && (
                                <div className={styles.clearIcon}>
                                    <Icon name="x-close" onPress={handleClearRetailer} />
                                </div>
                            )}
                        </Typeahead>
                    </div>
                    <VinVehicleDetails
                        isVinCheckLoading={isVinCheckLoading}
                        isVinCheckButtonDisabled={isVinCheckButtonDisabled}
                        onSelectedVehicle={selectVehicle}
                        onVehicleDetailsChange={setVehicleDetails}
                        onVinCheck={validateVin}
                        onVinInputChange={setVinInputValue}
                        possibleVehicles={possibleVehicles}
                        vehicleDetails={vehicleDetails}
                        vinError={vinError}
                        vinInputValue={vinInputValue}
                    />
                </Modal.Body>
                <Modal.Footer>
                    <div className="d-flex justify-content-between w-100">
                        <Button variant="ghost-blue" onPress={() => handleBackButtonClick('disclaimer', true)}>
                            Back
                        </Button>
                        <div className="d-flex">
                            <div className="mr-2">
                                <Button variant="ghost-blue" onPress={handleHide}>
                                    Cancel
                                </Button>
                            </div>
                            <Button
                                variant="primary"
                                onPress={handleRequestNewCase}
                                isLoading={isLoadingExistingCases || isCreatingCase}
                                disabled={isCreateCaseButtonDisabled}
                            >
                                Create Techline Case
                            </Button>
                        </div>
                    </div>
                </Modal.Footer>
            </React.Fragment>
        );
    };

    const renderReviewPreviousCases = () => (
        <CaseListModalContent
            cases={existingCases}
            isCreateCaseLoading={isCreatingCase}
            onBack={() => handleBackButtonClick('selectRetailerAndVin', false)}
            onCreate={handleConfirmNewCase}
            onEdit={handleEditPreviousCase}
            onHide={handleHide}
        />
    );

    return (
        <Modal show={show} centered size="lg" onHide={handleHide}>
            {currentFormType === 'disclaimer' && renderCaseDisclaimerModal()}
            {currentFormType === 'selectRetailerAndVin' && renderSelectRetailerVinDetails()}
            {currentFormType === 'allCaseTypes' && renderAllCaseTypes()}
            {currentFormType === 'reviewPreviousCases' && renderReviewPreviousCases()}
        </Modal>
    );
};
